import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { Link } from "react-router-dom";

import { graphql } from "@/gql";
import useDebounce from "@/hooks/useDebounce";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { theme } from "@/theme/customTheme";
import { displayError } from "@/utils/errors";

import NoTemplateView from "./components/NoTemplateView";
import TemplateItem from "./components/TemplateItem";

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 2),
}));

export const mealTemplatesPageQuery = graphql(`
  query MealTemplatesPage($name: String!) {
    mealPlanTemplates(name: $name) {
      nodes {
        ...TemplateItem
      }
    }
  }
`);

const deleteTemplateMutation = graphql(`
  mutation DeleteTemplate($id: ID!) {
    deleteMealPlanTemplate(input: { id: $id }) {
      mealPlanTemplate {
        id
      }
    }
  }
`);

const duplicateTemplateMutation = graphql(`
  mutation DuplicateTemplate($id: ID!) {
    duplicateMealPlanTemplate(input: { id: $id }) {
      mealPlanTemplate {
        id
      }
    }
  }
`);

const MealTemplatesPage: React.FC = () => {
  const [search, _setSearch] = useState("");
  const setSearch = useDebounce(_setSearch, 500);
  const snackbar = useGlobalSnackbar();
  const { data, refetch } = useQuery(mealTemplatesPageQuery, {
    variables: { name: search },
  });
  const [deleteTemplate] = useMutation(deleteTemplateMutation);
  const [duplicateTemplate] = useMutation(duplicateTemplateMutation);

  const templates = data?.mealPlanTemplates.nodes ?? [];

  function handleDelete(id: string) {
    return deleteTemplate({ variables: { id } })
      .then(() => {
        snackbar.open("Template deleted successfully");
        refetch();
      })
      .catch((e) => {
        snackbar.open(displayError(e, "Unable to delete template"));
      });
  }

  function handleDuplicate(id: string) {
    return duplicateTemplate({ variables: { id } })
      .then(() => {
        snackbar.open("Template duplicated successfully");
        refetch();
      })
      .catch((e) => {
        snackbar.open(displayError(e, "Unable to duplicate template"));
      });
  }

  return (
    <>
      <Root>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Meal Templates
          </Typography>
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Box marginTop={1} marginRight={theme.spacing(1)}>
              <TextField
                onChange={(e) => setSearch(e.target.value)}
                type="search"
                placeholder="Search..."
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" />,
                }}
              />
            </Box>
            <Box marginTop={1}>
              <Button
                component={Link}
                to="/templates/meals/new"
                startIcon={<AddIcon fontSize="small" />}
                variant="outlined"
              >
                Create template
              </Button>
            </Box>
          </Box>
        </Box>
      </Root>
      <Grid container spacing={2}>
        {templates.map((template) => (
          <Grid key={template.id} item xs={12} lg={6}>
            <TemplateItem
              id={template.id}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
            />
          </Grid>
        ))}
        {isEmpty(templates) && (
          <Grid item xs={12}>
            <NoTemplateView searchString={search} />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default MealTemplatesPage;
